// 공통 부품 — 상태색은 점·좌측 띠·알약 배지 세 가지 표현만(SCREENS §0)
import { useSyncExternalStore, type CSSProperties, type ReactNode } from "react";
import { studentColor, statusLabel, statusVar } from "../data/select";
import type { Student } from "../data/types";

export function Dot({ color, size }: { color: string; size?: number }) {
  return <span className="dot" style={{ background: color, ...(size ? { width: size, height: size } : {}) }} aria-hidden="true" />;
}

export function Pill({ children, color, solid }: { children: ReactNode; color: string; solid?: boolean }) {
  return <span className={"pill" + (solid ? " pill-solid" : "")} style={{ "--pill": color } as CSSProperties}>{children}</span>;
}

export const StatusPill = ({ status }: { status: string }) => <Pill color={statusVar(status)}>{statusLabel(status)}</Pill>;

export function StatTile({ n, label, color }: { n: number; label: string; color?: string }) {
  return (
    <div className="stat" style={color ? ({ "--stat": color } as CSSProperties) : undefined}>
      <span className="stat-n">{n}</span>
      <span className="stat-l">{label}</span>
    </div>
  );
}

export function StudentName({ st, school = true }: { st: Student; school?: boolean }) {
  return (
    <span className="who">
      <Dot color={studentColor(st.color)} />
      <span className="who-name" title={st.name}>{st.name}</span>
      {school && st.school ? <span className="who-school" title={st.school}>{st.school}</span> : null}
    </span>
  );
}

export function Section({ title, count, warn, action, children }: { title: string; count?: number; warn?: boolean; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <header className="section-h">
        <h2>{title}</h2>
        {count !== undefined ? <span className={"count" + (warn && count > 0 ? " count-warn" : "")}>{count}</span> : null}
        <span className="grow" />
        {action}
      </header>
      {children}
    </section>
  );
}

export const Empty = ({ children }: { children: ReactNode }) => <p className="empty">{children}</p>;

/* ───────── 토스트(되돌리기 버튼 포함) ───────── */
type Toast = { id: number; msg: string; tone: "info" | "error" | "ok"; undo?: () => void };
let toasts: Toast[] = [];
const tl = new Set<() => void>();
let seq = 0;
export function toast(msg: string, opt: { tone?: Toast["tone"]; undo?: () => void } = {}) {
  const t: Toast = { id: ++seq, msg, tone: opt.tone ?? "ok", undo: opt.undo };
  toasts = [...toasts.slice(-2), t];
  tl.forEach((f) => f());
  setTimeout(() => dismiss(t.id), opt.undo ? 8000 : 3500);
}
function dismiss(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  tl.forEach((f) => f());
}

export function Toasts() {
  const list = useSyncExternalStore((f) => { tl.add(f); return () => tl.delete(f); }, () => toasts);
  if (!list.length) return null;
  return (
    <div className="toasts" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={"toast toast-" + t.tone}>
          <span className="toast-msg">{t.msg}</span>
          {t.undo ? <button type="button" className="toast-undo" onClick={() => { t.undo!(); dismiss(t.id); }}>되돌리기</button> : null}
        </div>
      ))}
    </div>
  );
}
